import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "./AuthContext";

import NavBar from "./Components/NavBar";
import WelcomeMessage from "./Components/WelcomeMessages";
import Login from "./Components/Login";
import AdminPanel from "./Components/AdminPanel";
import BatchImageViewer from "./Components/Viewer";
import Settings from "./Components/Settings";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center pl-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// MainContent Component
const MainContent = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated()) {
    return (
      <div className="text-center text-gray-600">
        Please log in to access all features
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="flex-1 p-6"
      >
        <Routes>
          <Route path="/" element={<WelcomeMessage />} />
          <Route path="/batch/:ridName/:batchName" element={<BatchImageViewer />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/settings" element= {<Settings />}/>
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname.substring(1) || "home";

  const handleNavbarHover = (expanded) => {
    setIsNavExpanded(expanded);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <NavBar
        onNavClick={(tab) => {
          // Navigation is now handled in NavBar component using useNavigate
        }}
        activeTab={currentPath}
        onHover={handleNavbarHover}
      />
      <motion.main
        animate={{
          paddingLeft: isNavExpanded ? "240px" : "64px",
          transition: { duration: 0.3, ease: "easeInOut" },
        }}
        className="flex-1 w-full"
      >
        {children}
      </motion.main>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* <Route path="*" element={<WelcomeMessage />} /> */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainContent />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Layout>
      </Router>
    </AuthProvider>
  );
};

export default App;
